'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { PackageCheck, TruckIcon, FileCheck, Building2, CircleDollarSign } from 'lucide-react';

const steps = [
  {
    title: 'Admission',
    description: 'Goods enter the FTZ and are documented in our inventory management system.',
    icon: PackageCheck,
  },
  {
    title: 'Storage',
    description: 'Products are stored in our secure, climate-controlled FTZ warehouse.',
    icon: Building2,
  },
  {
    title: 'Processing',
    description: 'Optional value-added services like assembly or repackaging.',
    icon: FileCheck,
  },
  {
    title: 'Distribution',
    description: 'Products are shipped domestically or internationally as needed.',
    icon: TruckIcon,
  },
  {
    title: 'Duty Payment',
    description: 'Duties are paid only when goods enter U.S. commerce.',
    icon: CircleDollarSign,
  },
];

export function FTZProcess() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '100px 0px',
  });

  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">How It Works</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            The FTZ Process
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700">
            Understanding the Foreign-Trade Zone process is key to maximizing its benefits. Here's how we manage your goods:
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <div className="absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 bg-brand-200" />
          
          {steps.map((step, index) => (
            <motion.div
              key={index}
              ref={ref}
              initial="hidden"
              animate={inView ? "show" : "hidden"}
              variants={fadeIn(index % 2 === 0 ? 'left' : 'right')}
              className={`relative flex items-center gap-8 mb-12 ${
                index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
              }`}
            >
              <div className={`w-1/2 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-700">{step.description}</p>
                </div>
              </div>
              
              <div className="absolute left-1/2 -translate-x-1/2 w-12 h-12 bg-brand-600 rounded-full flex items-center justify-center text-white">
                <step.icon size={24} />
              </div>
              
              <div className="w-1/2" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}